import {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {useAuth} from '../../modules/auth'
import {BoardingPassCard} from '../../components/design-system'
import {api, Application, Booking, DoorCode, ApiError} from '../../services/api'
import {useToast} from '../../components/Toast'
import {PageHead} from './ui'
import {PasscodeHeader, PrivateAccessCards} from './PrivateAccess'
import {BedWashCard} from './BedWashCard'
import {PaymentCardSection} from './PaymentCardSection'
import {DEPOSIT} from '../public/site'

const ACTIVE_BOOKING: Booking['status'][] = ['PENDING', 'APPROVED', 'WAITLISTED']

const ACCENT = '#039540'
const INK = '#1b1b29'

/** Placeholder shown in place of the passcode until access unlocks. */
/** YYYY-MM-DD from a Date's *local* parts (toISOString would shift the day
 *  for any positive UTC offset, unlocking the code a day early). */
const localDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Shift a YYYY-MM-DD date string by whole days, staying in local time. */
const addDays = (isoDay: string, days: number): string => {
  const d = new Date(`${isoDay}T00:00:00`)
  if (!Number.isFinite(d.getTime())) return isoDay
  d.setDate(d.getDate() + days)
  return localDay(d)
}

/** "2026-09-14" → "Mon, Sep 14" for renter-facing copy. */
const formatDay = (isoDay: string): string => {
  const d = new Date(`${isoDay}T00:00:00`)
  if (!Number.isFinite(d.getTime())) return isoDay
  return d.toLocaleDateString(undefined, {weekday: 'short', month: 'short', day: 'numeric'})
}

const LockedPasscode: React.FC<{note: string}> = ({note}) => (
  <div className='ccp-fade-up' style={{display: 'flex', alignItems: 'center', gap: 14, background: '#fff', border: '1px dashed #d5d9e2', borderRadius: 18, padding: '20px 24px', marginBottom: 24}}>
    <div style={{width: 46, height: 46, borderRadius: 12, background: '#f4f6fa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0}}>🔒</div>
    <div>
      <div style={{fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9aa0b3'}}>Access passcode</div>
      <div style={{fontSize: 14, color: '#5a5f70', marginTop: 3, lineHeight: 1.5}}>{note}</div>
    </div>
  </div>
)

/**
 * Renter home — onboarding hub. Shows the renter's application status and the
 * next action (apply / under review / approved / resubmit). Bookings and bed
 * pass land here in later modules.
 */
const RenterHome = () => {
  const {currentUser} = useAuth()
  const name = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ')
  const [app, setApp] = useState<Application | null>(null)
  const [booking, setBooking] = useState<Booking | null>(null)
  // The booking whose saved card the "Payment card" section manages — the active
  // stay, or (if none) the most recent booking that still holds a card.
  const [cardBooking, setCardBooking] = useState<Booking | null>(null)
  const [codes, setCodes] = useState<DoorCode[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    ;(async () => {
      try {
        // Returning from Stripe Checkout → settle the session now so the deposit
        // shows as paid immediately, rather than waiting on the async webhook
        // (which never reaches a local backend). Idempotent with the webhook.
        const params = new URLSearchParams(window.location.search)
        const sessionId = params.get('session_id')
        if (sessionId) {
          await api.confirmStripeSession(sessionId).catch(() => {})
          // Strip the query so a refresh doesn't re-run the confirmation.
          window.history.replaceState({}, '', window.location.pathname)
        }
        const a = await api.getMyApplication()
        setApp(a)
        if (a?.status === 'APPROVED') {
          const mine = await api.myBookings()
          // Prefer the confirmed (APPROVED) stay so it isn't hidden behind an
          // older pending/waitlisted request when a renter has more than one.
          const approved = mine.find((b) => b.status === 'APPROVED') ?? null
          const active = approved ?? mine.find((b) => ACTIVE_BOOKING.includes(b.status)) ?? null
          setBooking(active)
          // Show the card section for the active stay, else the booking that
          // still holds a card, else the most recent booking (so the "No card on
          // file · Save card" prompt still appears). Null only if never booked.
          setCardBooking(active ?? mine.find((b) => b.cardOnFile) ?? mine[0] ?? null)
          // Access passcode + private info unlock only once the reservation is
          // confirmed — deposit received AND approved, per the brief's flow.
          if (approved && Number(approved.depositHeld) > 0) {
            setCodes(await api.doorCodes().catch(() => []))
          }
        }
      } catch (e) {
        if (!(e instanceof ApiError)) throw e
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  const toast = useToast()
  const cancelBooking = async () => {
    if (!booking) return
    try {
      await api.cancelBooking(booking.id)
      setBooking(null)
      toast.success('Booking request cancelled.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not cancel')
    }
  }

  // Rent must be current: OK if not yet on a subscription, active, or still
  // within the paid-through / move-out window; suspended once that passes.
  const today = new Date().toISOString().slice(0, 10)
  const sub = booking?.subscriptionStatus
  const rentAccessOk = !sub || sub === 'active' || (!!booking?.accessUntil && booking.accessUntil >= today)
  const depositOk = !loading && booking?.status === 'APPROVED' && Number(booking?.depositHeld ?? 0) > 0
  // Confirmed = approved + deposit received + rent current. Only then does the
  // access passcode and private detail unlock.
  const reservationConfirmed = depositOk && rentAccessOk
  // Rent lapsed (payment failed / subscription ended past its access window).
  const rentSuspended = depositOk && !rentAccessOk
  // Verified with a booking under way, but not yet unlocked (no deposit / pending).
  const accessLocked = !loading && !depositOk && (!!booking || app?.status === 'APPROVED')
  // The door code is released the day before move-in (the server withholds it
  // until then, so an early stay simply gets an empty list back).
  const unlockDate = booking?.startDate ? addDays(booking.startDate, -1) : null
  const beforeUnlock = !!unlockDate && localDay(new Date()) < unlockDate

  return (
    <div>
      {/* Current Access Passcode — predominant, at the very top, for approved
          renters with a confirmed reservation only. */}
      {reservationConfirmed && codes.length > 0 && <PasscodeHeader codes={codes} />}
      {reservationConfirmed && codes.length === 0 && (
        beforeUnlock
          ? <LockedPasscode note={`Your door access passcode unlocks on ${formatDay(unlockDate!)} — the day before you move in.`} />
          : <LockedPasscode note='No access passcode has been set yet — your admin will add it shortly.' />
      )}
      {rentSuspended && <LockedPasscode note="Access is paused — your monthly rent is overdue. Update your card on the Payments page to restore access." />}
      {accessLocked && <LockedPasscode note="Your door access passcode appears here once your $100 refundable deposit is received and your reservation is approved." />}

      <PageHead
        icon='✈️'
        eyebrow='Crew Crashpad'
        title={`Welcome aboard${name ? `, ${currentUser?.first_name}` : ''}`}
        sub='Your crashpad home — track your stay, payments and updates in one place.'
      />

      <div className='ccp-fade-up-2'>
      {/* Your card on file — save once, then deposit/rent/bed wash charge it. */}
      {cardBooking && <PaymentCardSection booking={cardBooking} />}

      {/* Active stay → the booking confirmation and the bed-wash card share a row
          (two columns on desktop, stacked on mobile). Otherwise a single column. */}
      {!loading && booking?.status === 'APPROVED' ? (
        <div className='row g-3' style={{marginBottom: 24}}>
          <div className='col-md-6'>
            <BookingSection booking={booking} onCancel={cancelBooking} />
          </div>
          <div className='col-md-6'>
            <BedWashCard cardOnFile={booking.cardOnFile} bookingId={booking.id} fill />
          </div>
        </div>
      ) : (
        !loading && (app?.status === 'APPROVED' ? <BookingSection booking={booking} onCancel={cancelBooking} /> : <ApplicationStatus app={app} />)
      )}

      <div style={{marginTop: 28}}>
        <BoardingPassCard
          title={name || 'Crew Member'}
          subtitle={currentUser?.email}
          docType={booking?.status === 'APPROVED' ? 'Bed Pass' : 'Crew Pass'}
          status={{
            label: booking?.status === 'APPROVED' ? 'Checked in' : app?.status === 'APPROVED' ? 'Verified' : 'Signed in',
            tone: booking?.status === 'APPROVED' || app?.status === 'APPROVED' ? 'available' : 'neutral',
          }}
          fields={[
            {label: 'Role', value: 'Renter'},
            {label: 'Verification', value: app?.status === 'APPROVED' ? 'Approved' : app ? app.status.charAt(0) + app.status.slice(1).toLowerCase() : 'Not started'},
          ]}
          stubCode={booking?.status === 'APPROVED' && booking.bed ? booking.bed.code : '✈'}
          stubLabel={booking?.status === 'APPROVED' ? 'Bed' : 'Crashpad'}
        />
      </div>

      {booking && (
        <div style={{marginTop: 16, textAlign: 'center'}}>
          <Link to='/booking' style={{color: ACCENT, fontWeight: 700, fontSize: 14, textDecoration: 'none'}}>
            View booking timeline →
          </Link>
        </div>
      )}

      {reservationConfirmed && <PrivateAccessCards booking={booking} />}
      </div>
    </div>
  )
}

const Banner: React.FC<{tone: 'green' | 'amber' | 'red'; title: string; text: string; action?: React.ReactNode}> = ({tone, title, text, action}) => {
  const colors = {
    green: {bg: 'rgba(3,149,64,0.08)', border: 'rgba(3,149,64,0.3)', accent: ACCENT, icon: '✅'},
    amber: {bg: 'rgba(255,168,0,0.1)', border: 'rgba(255,168,0,0.4)', accent: '#b8770a', icon: '⏳'},
    red: {bg: 'rgba(217,33,78,0.07)', border: 'rgba(217,33,78,0.3)', accent: '#d9214e', icon: '⚠️'},
  }[tone]
  return (
    <div style={{background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: '22px 24px', display: 'flex', gap: 16, alignItems: 'flex-start', height: '100%', boxSizing: 'border-box'}}>
      <div style={{fontSize: 26}}>{colors.icon}</div>
      <div style={{flex: 1}}>
        <h3 style={{fontSize: 17, fontWeight: 800, color: colors.accent, margin: '0 0 4px'}}>{title}</h3>
        <p style={{color: INK, fontSize: 14, lineHeight: 1.6, margin: 0}}>{text}</p>
        {action && <div style={{marginTop: 14}}>{action}</div>}
      </div>
    </div>
  )
}

const cta: React.CSSProperties = {display: 'inline-block', textDecoration: 'none', fontWeight: 700, fontSize: 14, color: '#fff', background: ACCENT, borderRadius: 10, padding: '10px 22px'}
const cancelBtn: React.CSSProperties = {fontWeight: 600, fontSize: 13, color: '#d9214e', background: 'rgba(217,33,78,0.1)', border: 'none', borderRadius: 9, padding: '9px 18px', cursor: 'pointer'}

const BookingSection: React.FC<{booking: Booking | null; onCancel: () => void}> = ({booking, onCancel}) => {
  if (!booking) {
    return (
      <Banner
        tone='green'
        title="You're verified ✈️"
        text='Ready to rest easy between flights? Browse the crashpad’s eight beds and reserve one — an admin will confirm it.'
        action={<Link to='/rooms' style={cta}>Reserve a bed</Link>}
      />
    )
  }
  const bed = booking.bed?.code ?? 'a bed'
  if (booking.status === 'PENDING') {
    // Deposit already received → don't ask them to pay again; just await approval.
    const depositPaid = Number(booking.depositHeld) > 0
    if (depositPaid) {
      return (
        <Banner
          tone='amber'
          title='Deposit received — under review ⏳'
          text={`Your $100 refundable deposit for bed ${bed} (move-in ${booking.startDate}) is in. Your reservation is now under review — please allow at least 24 hours for approval. Login and access information unlock once approved.`}
          action={<button onClick={onCancel} style={cancelBtn}>Cancel request</button>}
        />
      )
    }
    return (
      <Banner
        tone='amber'
        title='Booking requested'
        text={`Your request for bed ${bed} (move-in ${booking.startDate}) is under review. ${DEPOSIT.warning}`}
        action={
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 10}}>
            <Link to='/payments' style={cta}>Pay $100 deposit</Link>
            <button onClick={onCancel} style={cancelBtn}>Cancel request</button>
          </div>
        }
      />
    )
  }
  if (booking.status === 'WAITLISTED') {
    return <Banner tone='amber' title="You're on the waitlist" text={`Bed ${bed} is full right now — we'll notify you when it frees up.`} action={<button onClick={onCancel} style={cancelBtn}>Cancel request</button>} />
  }
  // APPROVED — open-ended monthly stay. Show the monthly rate, never a total.
  const rate = booking.unitPrice ? `$${Number(booking.unitPrice).toFixed(0)}/month` : ''
  const dates = `Move-in ${booking.startDate}${booking.moveOutDate ? ` · move-out ${booking.moveOutDate}` : ''}`
  const cost = rate ? ` · ${rate}, billed monthly` : ''
  const checkedIn = booking.checkedInAt ? ' You are checked in.' : ''
  const rentAction = booking.hasSubscription
    ? <Link to='/payments' style={cta}>Manage rent</Link>
    : <Link to='/payments' style={cta}>Set up monthly rent</Link>
  return (
    <Banner
      tone='green'
      title={`Bed ${bed} is yours 🎉`}
      text={`Your booking is confirmed. ${dates}${cost}.${checkedIn}`}
      action={rentAction}
    />
  )
}

const ApplicationStatus: React.FC<{app: Application | null}> = ({app}) => {
  if (!app) {
    return (
      <Banner
        tone='amber'
        title='Get verified to book'
        text="You're not verified yet. Complete your renter application — airline badge, government ID, and emergency contact — and an admin will review it."
        action={<Link to='/apply' style={cta}>Start application</Link>}
      />
    )
  }
  if (app.status === 'PENDING') {
    return (
      <Banner
        tone='amber'
        title='Application under review'
        text="Thanks! Your application is with our team. We'll email you as soon as it's reviewed."
      />
    )
  }
  if (app.status === 'APPROVED') {
    return (
      <Banner
        tone='green'
        title="You're verified ✈️"
        text='Your application is approved. Booking a bed will be available here shortly.'
      />
    )
  }
  return (
    <Banner
      tone='red'
      title='Application not approved'
      text={app.rejectionReason ? `Reason: ${app.rejectionReason}` : 'Your application was not approved. You can update your details and resubmit.'}
      action={<Link to='/apply' style={cta}>Update & resubmit</Link>}
    />
  )
}

export {RenterHome}
